import { useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Package, Upload, AlertTriangle, ClipboardList, Settings as Cog, LogOut, Wrench, Inbox, Users, Tags, LayoutTemplate, CreditCard, Bot, Rocket, Mail, Truck, Menu as MenuIcon, GalleryHorizontal, MonitorSmartphone, PanelTop, Type, Factory } from "lucide-react";
import SuppliersTab from "@/pages/admin/suppliers/SuppliersTab";
import DashboardTab from "@/pages/admin/DashboardTab";
import ProductsTab from "@/pages/admin/ProductsTab";
import CatalogTab from "@/pages/admin/CatalogTab";
import HomepageTab from "@/pages/admin/HomepageTab";
import MenuBuilderTab from "@/pages/admin/MenuBuilderTab";
import SlidesTab from "@/pages/admin/SlidesTab";
import ResponsiveTab from "@/pages/admin/ResponsiveTab";
import HeaderFooterTab from "@/pages/admin/HeaderFooterTab";
import ProductWordingTab from "@/pages/admin/ProductWordingTab";
import ImportTab from "@/pages/admin/ImportTab";
import ReviewsTab from "@/pages/admin/ReviewsTab";
import OrdersTab from "@/pages/admin/OrdersTab";
import SettingsTab from "@/pages/admin/SettingsTab";
import PaymentSettingsTab from "@/pages/admin/PaymentSettingsTab";
import ShippingTab from "@/pages/admin/shipping/ShippingTab";
import EnquiriesTab from "@/pages/admin/EnquiriesTab";
import CustomersTab from "@/pages/admin/CustomersTab";
import ScraperTab from "@/pages/admin/ScraperTab";
import SeoTab from "@/pages/admin/SeoTab";
import { EmailHealthBanner } from "@/components/admin/EmailHealth";
import EmailSettingsTab from "@/pages/admin/EmailSettingsTab";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, C: DashboardTab },
  { id: "orders", label: "Orders", icon: ClipboardList, C: OrdersTab },
  { id: "customers", label: "Customers", icon: Users, C: CustomersTab },
  { id: "products", label: "Products", icon: Package, C: ProductsTab },
  { id: "catalog", label: "Categories & Brands", icon: Tags, C: CatalogTab },
  { id: "homepage", label: "Homepage Blocks", icon: LayoutTemplate, C: HomepageTab },
  { id: "slides", label: "Homepage Slides", icon: GalleryHorizontal, C: SlidesTab },
  { id: "menu", label: "Mega Menu", icon: MenuIcon, C: MenuBuilderTab },
  { id: "headerfooter", label: "Header & Footer", icon: PanelTop, C: HeaderFooterTab },
  { id: "wording", label: "Product Wording", icon: Type, C: ProductWordingTab },
  { id: "responsive", label: "Responsive Design", icon: MonitorSmartphone, C: ResponsiveTab },
  { id: "suppliers", label: "Suppliers", icon: Factory, C: SuppliersTab },
  { id: "import", label: "Supplier Import", icon: Upload, C: ImportTab },
  { id: "scrapers", label: "Supplier Scrapers", icon: Bot, C: ScraperTab },
  { id: "seo", label: "SEO Management", icon: Rocket, C: SeoTab },
  { id: "reviews", label: "Price Reviews", icon: AlertTriangle, C: ReviewsTab },
  { id: "enquiries", label: "Enquiries", icon: Inbox, C: EnquiriesTab },
  { id: "payments", label: "Payment Settings", icon: CreditCard, C: PaymentSettingsTab },
  { id: "shipping", label: "Shipping Management", icon: Truck, C: ShippingTab },
  { id: "email", label: "Email Settings", icon: Mail, C: EmailSettingsTab },
  { id: "settings", label: "Commerce Settings", icon: Cog, C: SettingsTab },
];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);
  useEffect(() => {
    // label every table cell with its column heading so tables can stack into cards on phones
    const label = () => document.querySelectorAll(".admin-main table").forEach((t) => {
      const heads = [...t.querySelectorAll("thead th")].map((th) => th.textContent.trim());
      if (!heads.length) return;
      t.querySelectorAll("tbody tr").forEach((tr) => [...tr.children].forEach((td, i) => { if (heads[i] && td.dataset.label !== heads[i]) td.dataset.label = heads[i]; }));
    });
    label();
    const mo = new MutationObserver(label);
    const el = document.querySelector(".admin-main");
    if (el) mo.observe(el, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [tab, user]);

  if (user === null || user === false)
    return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  const Active = TABS.find((t) => t.id === tab).C;
  const pick = (id) => { setTab(id); setNavOpen(false); window.scrollTo(0, 0); };

  return (
    <div className="min-h-screen bg-background lg:flex">
      <div className="lg:hidden sticky top-0 z-40 flex items-center gap-3 h-14 px-4 bg-steel text-white" data-testid="admin-mobile-bar">
        <button onClick={() => setNavOpen(true)} aria-label="Open admin menu" className="grid place-items-center w-10 h-10 rounded-md bg-steel-light" data-testid="admin-mobile-menu"><MenuIcon size={20} /></button>
        <span className="font-heading font-extrabold">ACP ADMIN</span>
        <span className="ml-auto text-sm text-slate-300 truncate">{TABS.find((t) => t.id === tab).label}</span>
      </div>
      {navOpen && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setNavOpen(false)} />}
      <aside className={`w-64 lg:w-60 shrink-0 bg-steel text-white flex flex-col h-screen fixed lg:sticky top-0 left-0 z-50 transition-transform lg:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center gap-2 px-5 h-16 border-b border-steel-light">
          <span className="grid place-items-center w-8 h-8 rounded-md bg-accent text-steel"><Wrench size={18} strokeWidth={2.5} /></span>
          <div><div className="font-heading font-extrabold">ACP ADMIN</div></div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => pick(t.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                tab === t.id ? "bg-accent text-steel" : "text-slate-300 hover:bg-steel-light"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-steel-light">
          <div className="px-3 py-2 text-xs text-slate-400 truncate">{user.email}</div>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-300 hover:bg-steel-light">
            <LogOut size={18} /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-3 sm:p-6 lg:p-8 max-w-[1600px] admin-main">
        <EmailHealthBanner />
        <Active />
      </main>
    </div>
  );
}
